/**
 * Preview retry policy.
 *
 * A preview sandbox can be "cold": the container was reclaimed after idle and
 * its dev server is no longer listening. Recovering requires an explicit
 * redeploy (WebSocket `preview` message), which spins up a NEW instance with a
 * NEW preview URL. Build-first templates (e.g. Astro) can take well over a
 * minute to revive (container wake + install + build + dev server), so the
 * iframe must:
 *   1. keep polling long enough for a slow cold revive to complete, and
 *   2. re-request the redeploy periodically in case an earlier request did not
 *      revive the sandbox.
 *
 * These are pure functions so the policy is unit-testable without a DOM.
 */

/** Attempts using exponential backoff before switching to steady slow polling. */
export const MAX_FAST_RETRIES = 15;
/** Steady polling interval (ms) once the fast-backoff phase is exhausted. */
export const SLOW_RETRY_DELAY = 15000;
/**
 * Hard stop. Fast phase (~100s) + slow phase at {@link SLOW_RETRY_DELAY} gives
 * a cold build-first sandbox generous time (~12 min) to revive before the
 * iframe surfaces a terminal error. The manual refresh remains available.
 */
export const MAX_TOTAL_RETRIES = 60;
/** First failed-attempt count at which a redeploy is requested. */
export const REDEPLOY_AFTER_ATTEMPT = 3;
/**
 * After the first redeploy request, re-request every N further attempts so a
 * sandbox that failed to revive keeps getting revive requests.
 */
export const REDEPLOY_RETRY_INTERVAL = 8;

/** Delay (ms) before the next availability check for a given zero-based attempt. */
export function getRetryDelay(attempt: number): number {
	if (attempt >= MAX_FAST_RETRIES) {
		return SLOW_RETRY_DELAY;
	}
	// 1s, 2s, 4s, 8s (capped)
	return Math.min(1000 * Math.pow(2, attempt), 8000);
}

/**
 * Whether a redeploy should be requested at this upcoming attempt. Fires at
 * {@link REDEPLOY_AFTER_ATTEMPT}, then every {@link REDEPLOY_RETRY_INTERVAL}
 * attempts thereafter.
 */
export function shouldRequestRedeploy(nextAttempt: number): boolean {
	if (nextAttempt < REDEPLOY_AFTER_ATTEMPT) {
		return false;
	}
	return (nextAttempt - REDEPLOY_AFTER_ATTEMPT) % REDEPLOY_RETRY_INTERVAL === 0;
}

/** Whether the iframe should give up and surface a terminal error. */
export function isTerminalAttempt(attempt: number): boolean {
	return attempt >= MAX_TOTAL_RETRIES;
}
